import React, { useState, useRef } from 'react';
import { FaCalendarAlt, FaDownload, FaFileAlt, FaUsers, FaClock, FaCheckCircle, FaTimesCircle, FaFilter, FaFilePdf, FaFileExcel } from 'react-icons/fa';

const AttendanceReport = () => {
  const selectedGroup = localStorage.getItem('selectedGroup');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [attendanceData, setAttendanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showData, setShowData] = useState(false);
  const tableRef = useRef(null);

  // Sample groups data
  const groups = [
    { id: 'all', name: 'All Groups' },
    { id: 'engineering', name: 'Engineering Team' },
    { id: 'marketing', name: 'Marketing Team' },
    { id: 'sales', name: 'Sales Team' },
    { id: 'hr', name: 'Human Resources' },
    { id: 'finance', name: 'Finance Team' }
  ];

  // Sample attendance data generator
  const generateAttendanceData = () => {
    const employees = [
      { name: 'John Smith', group: 'Engineering Team' },
      { name: 'Sarah Johnson', group: 'Marketing Team' },
      { name: 'Mike Davis', group: 'Engineering Team' },
      { name: 'Emily Brown', group: 'Sales Team' },
      { name: 'David Wilson', group: 'Human Resources' },
      { name: 'Lisa Anderson', group: 'Finance Team' },
      { name: 'James Miller', group: 'Engineering Team' },
      { name: 'Anna Garcia', group: 'Marketing Team' },
      { name: 'Robert Taylor', group: 'Sales Team' },
      { name: 'Maria Rodriguez', group: 'Human Resources' }
    ];
    
    const data = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      employees.forEach((employee, index) => {
        // Skip weekends for more realistic data
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        if (isWeekend) return;
        
        // Filter by selected group
        if (selectedGroup !== 'all' && employee.group !== groups.find(g => g.id === selectedGroup)?.name) {
          return;
        }
        
        const attendanceRate = 0.85; // 85% attendance rate
        const isPresent = Math.random() < attendanceRate;
        const timeIn = isPresent ? `${8 + Math.floor(Math.random() * 2)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')} AM` : '-';
        const timeOut = isPresent ? `${5 + Math.floor(Math.random() * 3)}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')} PM` : '-';
        
        data.push({
          id: `${employee.name.replace(' ', '')}_${d.toISOString().split('T')[0]}`,
          employee: employee.name,
          date: d.toISOString().split('T')[0],
          group: employee.group,
          status: isPresent ? 'Present' : 'Absent',
          timeIn,
          timeOut,
          hoursWorked: isPresent ? `${7 + Math.floor(Math.random() * 3)}.${Math.floor(Math.random() * 6)}` : '0'
        });
      });
    }
    
    return data.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const generateReport = () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('Start date cannot be after end date');
      return;
    }

    setIsLoading(true);
    
    // Simulate loading
    setTimeout(() => {
      const data = generateAttendanceData();
      setAttendanceData(data);
      setShowData(true);
      setIsLoading(false);
    }, 1500);
  };

  // Statistical functions
  const getUniqueEmployees = () => {
    const uniqueEmployees = [...new Set(attendanceData.map(record => record.employee))];
    return uniqueEmployees.length;
  };

  const getPresentCount = () => {
    return attendanceData.filter(record => record.status === 'Present').length;
  };

  const getAbsentCount = () => {
    return attendanceData.filter(record => record.status === 'Absent').length;
  };

  const getAttendanceRate = () => {
    if (attendanceData.length === 0) return 0;
    return Math.round((getPresentCount() / attendanceData.length) * 100);
  };

  // Export functions
  const exportToPDF = () => {
    const printContent = tableRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Attendance Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #1f2937; text-align: center; margin-bottom: 30px; }
            .report-info { margin-bottom: 20px; padding: 10px; background: #f3f4f6; border-radius: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
            th { background-color: #f9fafb; font-weight: bold; }
            .present { color: #059669; font-weight: bold; }
            .absent { color: #dc2626; font-weight: bold; }
            @media print { 
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>Attendance Report</h1>
          <div class="report-info">
            <p><strong>Date Range:</strong> ${startDate} to ${endDate}</p>
            <p><strong>Group:</strong> ${groups.find(g => g.id === selectedGroup)?.name || 'All Groups'}</p>
            <p><strong>Total Records:</strong> ${attendanceData.length}</p>
            <p><strong>Attendance Rate:</strong> ${getAttendanceRate()}%</p>
          </div>
          ${printContent}
          <script>window.print(); window.close();</script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  const exportToExcel = () => {
    const headers = ['Employee Name', 'Date', 'Group', 'Status', 'Time In', 'Time Out', 'Hours Worked'];
    const csvContent = [
      headers.join(','),
      ...attendanceData.map(record => [
        record.employee,
        record.date,
        record.group,
        record.status,
        record.timeIn,
        record.timeOut,
        record.hoursWorked
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attendance-report-${startDate}-to-${endDate}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <FaCalendarAlt className="text-blue-600 text-xl mr-3" />
          <h1 className="text-2xl font-bold text-gray-800">Attendance Report</h1>
        </div>
        
        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <FaFilter className="text-gray-600 text-lg mr-2" />
            <h2 className="text-lg font-semibold text-gray-700">Filter Options</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            
          </div>
          
          <div className="flex justify-center">
            <button
              onClick={generateReport}
              disabled={isLoading}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <FaFilter className="text-sm mr-2" />
                  Generate Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {showData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500">
              <div className="flex items-center">
                <FaUsers className="text-blue-600 text-2xl mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Total Employees</p>
                  <p className="text-2xl font-bold text-gray-800">{getUniqueEmployees()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-green-500">
              <div className="flex items-center">
                <FaCheckCircle className="text-green-600 text-2xl mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Present</p>
                  <p className="text-2xl font-bold text-gray-800">{getPresentCount()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-red-500">
              <div className="flex items-center">
                <FaTimesCircle className="text-red-600 text-2xl mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Absent</p>
                  <p className="text-2xl font-bold text-gray-800">{getAbsentCount()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-yellow-500">
              <div className="flex items-center">
                <FaClock className="text-yellow-600 text-2xl mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Attendance Rate</p>
                  <p className="text-2xl font-bold text-gray-800">{getAttendanceRate()}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Buttons */}
        {showData && (
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={exportToPDF}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center"
            >
              <FaFilePdf className="text-sm mr-2" />
              Export to PDF
            </button>
            <button
              onClick={exportToExcel}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
            >
              <FaFileExcel className="text-sm mr-2" />
              Export to Excel
            </button>
          </div>
        )}

        {/* Attendance Table */}
        {showData && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Attendance Records ({attendanceData.length} entries)
              </h3>
              <p className="text-sm text-gray-600">
                Showing data from {formatDate(startDate)} to {formatDate(endDate)}
              </p>
            </div>
            
            <div className="overflow-x-auto max-h-96">
              <table ref={tableRef} className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hours Worked
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendanceData.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {record.employee}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(record.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.group}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {record.status === 'Present' ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FaCheckCircle className="text-xs mr-1" />
                            {record.status}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <FaTimesCircle className="text-xs mr-1" />
                            {record.status}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.timeIn}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.timeOut}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.hoursWorked !== '0' ? `${record.hoursWorked}h` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {attendanceData.length === 0 && showData && (
              <div className="text-center py-8">
                <FaUsers className="mx-auto text-gray-400 text-4xl mb-2" />
                <p className="text-gray-500">No attendance records found for the selected criteria.</p>
              </div>
            )}
          </div>
        )}

        {/* No data message */}
        {!showData && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaCalendarAlt className="mx-auto text-gray-400 text-4xl mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Data to Display</h3>
            <p className="text-gray-500">
              Select a date range and group, then click "Generate Report" to view attendance data.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceReport;